import { useState } from 'react';

interface Destination {
  id: number;
  name: string;
  description: string;
  price: string;
  image: string;
}

const destinations: Destination[] = [
  {
    id: 1,
    name: 'Bear Mountain State Park',
    description: 'Scenic mountain views with full RV hookups and hiking trails',
    price: '$35',
    image: 'https://public.readdy.ai/ai/img_res/9558d62bc302db03586e406f792e5ea2.jpg',
  },
  {
    id: 2,
    name: 'Minnewaska State Park',
    description: 'Pristine lakes and waterfalls perfect for family camping',
    price: '$42',
    image: 'https://readdy.ai/api/search-image?query=Minnewaska%20State%20Park%20camping%20with%20pristine%20lake%20view%2C%20waterfall%20in%20distance%2C%20tent%20camping%20site%20near%20water%2C%20beautiful%20natural%20landscape%2C%20simple%20background%20emphasizing%20the%20scenic%20beauty&width=600&height=800&seq=dest-minnewaska-1&orientation=portrait',
  },
  {
    id: 3,
    name: 'Lake Taghkanic',
    description: 'Lakeside camping with swimming and boating activities',
    price: '$38',
    image: 'https://readdy.ai/api/search-image?query=Lake%20Taghkanic%20camping%20site%20by%20the%20water%2C%20lakeside%20RV%20camping%2C%20peaceful%20lake%20setting%20with%20trees%2C%20boats%20on%20water%2C%20serene%20camping%20atmosphere%2C%20simple%20clean%20background&width=600&height=800&seq=dest-taghkanic-1&orientation=portrait',
  },
  {
    id: 4,
    name: 'Catskill Mountains',
    description: 'Remote wilderness camping for the adventurous spirit',
    price: '$30',
    image: 'https://readdy.ai/api/search-image?query=Catskill%20Mountains%20wilderness%20camping%2C%20remote%20mountain%20campsite%2C%20tent%20in%20forest%20clearing%2C%20dramatic%20mountain%20peaks%2C%20adventurous%20camping%20scene%2C%20simple%20background%20highlighting%20nature&width=600&height=800&seq=dest-catskill-1&orientation=portrait',
  },
  {
    id: 5,
    name: 'Harriman State Park',
    description: 'Extensive trail network with secluded camping spots',
    price: '$40',
    image: 'https://readdy.ai/api/search-image?query=Harriman%20State%20Park%20camping%20area%2C%20secluded%20forest%20campsite%2C%20hiking%20trails%20through%20woods%2C%20peaceful%20camping%20environment%2C%20tall%20trees%20and%20natural%20setting%2C%20simple%20clean%20background&width=600&height=800&seq=dest-harriman-1&orientation=portrait',
  },
  {
    id: 6,
    name: 'Taconic State Park',
    description: 'Waterfall views and premium RV facilities',
    price: '$45',
    image: 'https://readdy.ai/api/search-image?query=Taconic%20State%20Park%20RV%20camping%20with%20waterfall%20view%2C%20premium%20campground%20facilities%2C%20modern%20RV%20site%20near%20cascading%20waterfall%2C%20beautiful%20natural%20scenery%2C%20simple%20background&width=600&height=800&seq=dest-taconic-1&orientation=portrait',
  },
];

const FeaturedDestinations = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  const handleScroll = (direction: 'left' | 'right') => {
    const container = document.getElementById('destinations-scroll');
    if (container) {
      const scrollAmount = 400;
      const newPosition = direction === 'left' 
        ? Math.max(0, scrollPosition - scrollAmount)
        : Math.min(container.scrollWidth - container.clientWidth, scrollPosition + scrollAmount);
      
      container.scrollTo({ left: newPosition, behavior: 'smooth' });
      setScrollPosition(newPosition);
    }
  };

  return (
    <section id="destinations" className="py-24 bg-sage-light">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-wider text-forest/70 mb-4">POPULAR SPOTS</p>
          <h2 className="font-serif text-6xl font-bold text-forest leading-tight">
            Where the Valley<br />
            Meets Adventure
          </h2>
        </div>

        <div className="relative">
          <button
            onClick={() => handleScroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-forest text-white rounded-full flex items-center justify-center hover:bg-forest-light transition-colors shadow-lg cursor-pointer"
            aria-label="Scroll left"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>

          <div
            id="destinations-scroll"
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {destinations.map((destination) => (
              <div
                key={destination.id}
                className="flex-shrink-0 w-80 group cursor-pointer"
                data-product-shop
              >
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 h-[480px]">
                  <img
                    src={destination.image}
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  <div className="absolute top-4 right-4 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-full">
                    <p className="text-white text-sm font-medium whitespace-nowrap">From {destination.price}/night</p>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6">
                    <h3 className="text-white font-bold text-2xl mb-2">{destination.name}</h3>
                    <p className="text-white/90 text-sm">{destination.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => handleScroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-forest text-white rounded-full flex items-center justify-center hover:bg-forest-light transition-colors shadow-lg cursor-pointer"
            aria-label="Scroll right"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedDestinations;